import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { PhoneNumberInput } from '@/components/PhoneNumberInput.tsx';
import {
  SmsConsentCheckboxes,
  smsConsentDefaultValues,
  smsConsentSchema,
} from '@/components/SmsConsentCheckboxes';
import { Button } from '@/components/ui/button.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { emailSchema, phoneNumberSchema } from '@/lib/validation-schemas.ts';

const formId = 'insurance-send-link-form';
const formSchema = z.object({
  phoneNumber: phoneNumberSchema,
  email: emailSchema,
  ...smsConsentSchema.shape,
});

export type InsuranceSendLinkFormData = z.infer<typeof formSchema>;

interface Props {
  onSubmit: (data: InsuranceSendLinkFormData) => void;
}

export function InsuranceSendLinkForm({ onSubmit }: Props) {
  const form = useForm<InsuranceSendLinkFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: '',
      email: '',
      ...smsConsentDefaultValues,
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
      <div className="space-y-6 sm:space-y-8">
        <p className="text-base leading-6 text-main">
          Still can&apos;t find it? We&apos;ll text and email you a reminder to pick up where you
          left off.
        </p>

        <FieldGroup>
          <div className="space-y-7">
            <Controller
              name="phoneNumber"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-phoneNumber`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Phone number</FieldLabel>
                    <PhoneNumberInput {...field} id={inputId} aria-invalid={fieldState.invalid} />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />

            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-email`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Email</FieldLabel>
                    <Input
                      {...field}
                      id={inputId}
                      type="email"
                      aria-invalid={fieldState.invalid}
                      placeholder="example@email.com"
                      inputMode="email"
                    />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />
          </div>
        </FieldGroup>

        <SmsConsentCheckboxes
          control={form.control}
          formId={formId}
          labelClassName="text-xs leading-snug text-charcoal-500"
        />

        <Button type="submit" className="w-full shadow-sm">
          Send me a reminder
        </Button>
      </div>
    </form>
  );
}
